'use client';

import { isIOS, isAndroid, isMobile, isTablet, isSafari, osVersion } from "react-device-detect";
import { OS, Platform, OS_VERSION_THRESHOLDS, VERSION_PATTERNS } from "@/lib/consts/device";
import { DeviceOrientation } from "@/lib/types/device";

export const getOs = (): OS => {
    if (isIOS) return OS.IOS;
    if (isAndroid) return OS.ANDROID;
    return OS.WEB;
}

export const getDeviceOrientation = (): DeviceOrientation => {
    return window.matchMedia('(orientation: portrait)').matches ? DeviceOrientation.PORTRAIT : DeviceOrientation.LANDSCAPE;
}

export const getPlatformData = (): Platform => {
    if (isTablet) return Platform.TABLET;
    if (isMobile) return Platform.MOBILE;
    return Platform.DESKTOP;
}

export const isOlderOsVersion = (): boolean => {
    if (typeof window === 'undefined') return false;

    const ua = navigator?.userAgent?.toLowerCase();
    if (!ua) return true;

    const isChromeIOS = VERSION_PATTERNS.CHROME_IOS_DETECT.test(ua);
    let versionMatch = ua.match(VERSION_PATTERNS.SAFARI_IOS);

    if (!versionMatch) {
        versionMatch = ua.match(VERSION_PATTERNS.CHROME_IOS);
    }

    if (!versionMatch) return false;

    const osMobileVersion = parseFloat(versionMatch[1]);
    if (isNaN(osMobileVersion)) return true;

    const isOldSafariIOS = (isSafari || isIOS) && osMobileVersion < OS_VERSION_THRESHOLDS.SAFARI_IOS;
    const isOldAndroid = isAndroid && osMobileVersion < OS_VERSION_THRESHOLDS.ANDROID;
    const isOldChromeIOS = isChromeIOS && osMobileVersion < OS_VERSION_THRESHOLDS.CHROME_IOS;

    return isOldSafariIOS || isOldAndroid || isOldChromeIOS;
}

export const isIOSVersionBelow = (version: number): boolean => {
    if (typeof window === 'undefined') return false;
    if (!isIOS) return false;

    const numericVersion = parseFloat(osVersion);
    if (isNaN(numericVersion)) return true;

    return numericVersion < version;
};

/**
 * iOS 26 replaced Safari's opaque chrome with translucent "Liquid Glass" bars
 * that sample whatever the page paints behind them, so any region the page
 * leaves unpainted reveals the layer underneath.
 *
 * `osVersion` cannot answer this: iOS 26 WebKit froze the UA platform token at
 * "CPU iPhone OS 18_6". Safari still advances its own `Version/` token in step
 * with the OS major, and third-party iOS browsers drop `Version/` but keep a
 * truthful `OS 26_x`. Webviews expose neither and are excluded on purpose —
 * they render no Safari chrome.
 */
export const isIOS26OrAbove = (): boolean => {
    if (typeof window === 'undefined') return false;
    if (!isIOS) return false;

    const ua = navigator?.userAgent ?? '';
    const safariVersionToken = ua.match(/version\/(\d+)/i);
    const osVersionToken = ua.match(/OS (\d+)[_.]/i);
    const majorVersion = parseInt(
        (safariVersionToken ?? osVersionToken)?.[1] ?? '',
        10
    );

    return !isNaN(majorVersion) && majorVersion >= 26;
};

export const isLandscape = () => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(orientation: landscape)').matches;
}
